import { useQuery } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { gql } from './__generated__';
import { Center, Loader, Stack, Text } from '@mantine/core';
import confetti from 'canvas-confetti';

const latestActiveProductDocument = gql(/* GraphQL */ `
  query LatestActiveProduct {
    latestActiveProduct {
      id
      code
      name
      price
      variants {
        id
        stock
      }
    }
  }
`);

const ConfettiCanvas = ({
  burstKey,
  canvasRef,
}: {
  burstKey: number;
  canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
}) => {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <canvas
      ref={(el) => {
        canvasRef.current = el;
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 2147483647,
      }}
      // Hidden when no burst pending — empty canvas costs nothing.
      data-burst={burstKey}
    />,
    document.body
  );
};

export const MonitorPage = () => {
  const prevTotalStockRef = useRef<number | null>(null);
  const prevProductIdRef = useRef<string | null>(null);
  const [burstKey, setBurstKey] = useState(0);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const fireRef = useRef<ReturnType<typeof confetti.create> | null>(null);

  // Manual test trigger: press 'c' or load /monitor?testConfetti=1.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.search.includes('testConfetti')) {
      setBurstKey((k) => k + 1);
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'c' || e.key === 'C') setBurstKey((k) => k + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Set explicit canvas drawing dimensions on mount (and on resize).
  // Cap at 1920×1080 even on a 4K TV — drawing surface 4× smaller, CSS stretches it visually.
  // At 3m viewing distance the upscale is invisible but the GPU has way less work per frame.
  useEffect(() => {
    const sizeCanvas = () => {
      const c = canvasRef.current;
      if (!c) return;
      c.width = Math.min(window.innerWidth, 1920);
      c.height = Math.min(window.innerHeight, 1080);
    };
    sizeCanvas();
    window.addEventListener('resize', sizeCanvas);
    return () => window.removeEventListener('resize', sizeCanvas);
  }, []);

  // Fire confetti when burstKey changes (>0). Tablet-style sustained 5-second burst.
  useEffect(() => {
    if (burstKey === 0) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!fireRef.current) {
      fireRef.current = confetti.create(canvas, { resize: true });
    }
    const fire = fireRef.current;

    // Tuned for LG TV: ~1000 total particles over the burst, with high scalar so
    // each one reads at 3m. Canvas is capped at 1080p so per-pixel cost stays bounded.
    const end = Date.now() + 4000;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const left = end - Date.now();
      if (left <= 0) return;
      const intensity = left / 4000;
      fire({
        particleCount: Math.floor(35 * intensity) + 25,
        spread: 110,
        startVelocity: 65,
        scalar: 2.4,
        ticks: 240,
        origin: { x: 0.5, y: 0.4 },
      });
      fire({
        particleCount: Math.floor(25 * intensity) + 18,
        angle: 60,
        spread: 75,
        startVelocity: 75,
        scalar: 2.0,
        ticks: 240,
        origin: { x: 0, y: 0.55 },
      });
      fire({
        particleCount: Math.floor(25 * intensity) + 18,
        angle: 120,
        spread: 75,
        startVelocity: 75,
        scalar: 2.0,
        ticks: 240,
        origin: { x: 1, y: 0.55 },
      });
      setTimeout(tick, 400);
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [burstKey]);

  const { data, loading } = useQuery(latestActiveProductDocument, { pollInterval: 3000 });

  // useEffect on data fires reliably on every Apollo poll update.
  useEffect(() => {
    const product = data?.latestActiveProduct;
    if (!product) return;
    const nextTotalStock = product.variants?.reduce((s, v) => s + v.stock, 0) ?? 0;
    const nextProductId = product.id;

    const prevStock = prevTotalStockRef.current;
    const prevId = prevProductIdRef.current;
    prevTotalStockRef.current = nextTotalStock;
    prevProductIdRef.current = nextProductId;

    if (
      prevId == null ||
      prevId !== nextProductId ||
      prevStock == null ||
      prevStock <= 0 ||
      nextTotalStock !== 0
    )
      return;

    setBurstKey((k) => k + 1);
  }, [data]);

  const renderInner = () => {
    if (loading && !data)
      return (
        <Center w="100vw" h="100vh" bg="white">
          <Loader size={32} />
        </Center>
      );

    if (!data?.latestActiveProduct)
      return (
        <Center w="100vw" h="100vh" bg="white">
          <Text fz="5vw" fw={700}>
            No active product
          </Text>
        </Center>
      );

    const { code, price, name, variants } = data.latestActiveProduct;
    const cleanName = name.replace(/\s*\([^)]*\)/g, '').trim();
    const totalStock = variants?.reduce((sum, v) => sum + v.stock, 0) ?? 0;
    const lowStock = totalStock > 0 && totalStock <= 10;

    return (
      <Center
        w="100vw"
        h="100vh"
        ta="center"
        bg={totalStock > 10 ? 'green' : totalStock > 0 ? 'orange' : 'red'}
        c="black"
        style={{
          transform: 'scaleX(-1)',
          userSelect: 'none',
          cursor: 'none',
          overflow: 'hidden',
        }}
      >
        <Stack align="center" gap="2vh">
          <Text
            fz="18vw"
            fw={900}
            maw="100vw"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              animation: lowStock ? 'lastPiecesPulse 1.6s ease-in-out infinite' : undefined,
            }}
          >
            K:{code} - {price}€
          </Text>
          <Text
            fz="10vw"
            fw={700}
            maw="100vw"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              animation: lowStock ? 'lastPiecesPulse 1.6s ease-in-out infinite' : undefined,
            }}
          >
            {cleanName}
          </Text>
          {totalStock <= 10 && (
            <Text
              fz="8vw"
              fw={600}
              maw="100vw"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                animation: lowStock ? 'lastPiecesPulse 1.6s ease-in-out infinite' : undefined,
              }}
            >
              {totalStock === 0 ? '! VYPREDANÉ !' : '! POSLEDNÉ KUSY !'}
            </Text>
          )}
        </Stack>
      </Center>
    );
  };

  return (
    <>
      <ConfettiCanvas burstKey={burstKey} canvasRef={canvasRef} />
      {renderInner()}
      <style>{`
        html, body {
          overflow: hidden !important;
          margin: 0;
        }
        html::-webkit-scrollbar, body::-webkit-scrollbar { display: none; }
        @keyframes lastPiecesPulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
            filter: drop-shadow(0 0 0 rgba(255,255,255,0));
          }
          50% {
            transform: scale(1.04);
            opacity: 0.82;
            filter: drop-shadow(0 0 14px rgba(255,255,255,0.55));
          }
        }
      `}</style>
    </>
  );
};
